import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';

import { SessionStorageService } from './session-storage.service';
import { UserService           } from './auth/user.service';
import { SortingOptionModel    } from '@shared/models';

@Injectable({
  providedIn: 'root'
})
export class QueryCollectorService {
  constructor(
    private sessionStorageService: SessionStorageService,
    private userService:           UserService
  ) { }

  getTimeFramesQuery(filters: any): string {
    return '?'+[
      ...this.getPaginationQuery(),
      ...this.getSearchQuery(),
      ...this.getPillsQuery(),
      ...this.getTimeFramesTableQuery(filters),
      ...this.getWorkingPeriodTableQuery(filters),
      ...this.getSortingQuery('external_name',    0),
      ...this.getSortingQuery('assignment_title', 1),
      ...this.getSortingQuery('started_at',       2),
      // ...this.getSortingQuery('external_employee_last_name',  0),
      // ...this.getSortingQuery('external_employee_first_name', 1),
      // ...this.getSortingQuery('assignment_title',             2),
      // ...this.getSortingQuery('started_at',                   3),
      filters.reporting ? ['reporting=1'] : []
    ].filter(q => q.length).join('&');
  }

  getHolidaysTimesQuery(from: Date = null, to: Date = null): string {
    let queryObj = {
      date_gteq: from ? formatDate(from, 'yyyy-MM-dd', 'de') : null,
      date_lteq: to   ? formatDate(to,   'yyyy-MM-dd', 'de') : null,
    };
    return '?'+[
      ...this.toQueryStringArray(queryObj, 'date'),
      ...['per_page=250',`page=1`]
    ].filter(q => q.length).join('&');
  }

  getActivityReportsQuery(): string {
    return '?'+[
      ...this.getDashboardStates('ar'),
      ...this.getPaginationQuery(),
      ...this.getSearchQuery(),
      ...this.getPillsQuery(),
      ...this.getSortingFlow()
    ].filter(q => q.length).join('&');
  }

  getFailedERPsQuery(betaFlag: string = null): string {
    let filters = this.sessionStorageService.headerFiltersValue.failed_erp;
    let dates   = this.sessionStorageService.failedERPDateFilters;

    return '?'+[
      ...this.getFailedERPFilter(Object.assign(filters, dates), true),
      ...this.getPaginationQuery(),
      ...this.getSearchQuery(),
      ...this.getPillsQuery(),
      ...this.getBetaQuery(betaFlag),
      ...this.getSortingFlow()
    ].filter(q => q.length).join('&');
  }

  getPhotoDocumentsQuery(): string {
    return '?'+[
      ...this.getPaginationQuery(),
      ...this.getSearchQuery(),
      ...this.getPillsQuery(),
      ...this.getSortingFlow()
    ].filter(q => q.length).join('&');
  }

  getCountersQuery(betaFlag: string = null): string {
    return '?'+[
      ...this.getPillsQuery(),
      ...this.getBetaQuery(betaFlag)
    ].filter(q => q.length).join('&');
  }

  getEBSCountersQuery(): string {
    return '?'+[
      ...this.getPillsQuery(),
      ...this.getActiveAssignmentsQuery()
    ].filter(q => q.length).join('&');
  }

  getActivityReportsArchiveQuery(): string {
    let filters = { ...this.sessionStorageService.activityReportsArchiveForm };

    return '?'+[
      ...this.getWorkingPeriodTableQuery(filters),
      ...this.getWorkingPeriodArchiveStates(filters),
      ...this.getExtraInfoQuery(filters),
      ...this.getFailedERPFilter(filters),
      ...this.getAssignmentsTableQuery(filters, 'assignment'),
      ...this.getExternalEmployeesTableQuery(filters),
      ...this.getTimeFramesTableQuery(filters),
      ...this.getPaginationQuery(),
      ...this.getSortingFlow(),
      ...this.getPillsQuery()
    ].filter(q => q.length).join('&');
  }

  getPhotoDocumentsArchiveQuery(): string {
    let filters = {
      ...this.sessionStorageService.photoDocumentsArchiveForm
    };
    return '?'+[
      ...this.getPhotoDocumentsTableQuery(filters),
      ...this.getPhotoDocumentsZvooveQuery(filters),
      ...this.getCheckboxStateFilter(filters, 'photo_documents'),
      ...this.getExternalEmployeesTableQuery(filters),
      ...this.getPaginationQuery(),
      ...this.getSortingFlow(),
      ...this.getPillsQuery()
    ].filter(q => q.length).join('&');
  }

  private getPhotoDocumentsTableQuery(filters): string[] {
    let queryObj = {
      archived_on_gteq:  filters.photoDocumentMovedToArchiveFrom ? formatDate(filters.photoDocumentMovedToArchiveFrom, 'yyyy-MM-dd', 'de') : null,
      archived_on_lteq:  filters.photoDocumentMovedToArchiveTo   ? formatDate(filters.photoDocumentMovedToArchiveTo,   'yyyy-MM-dd', 'de') : null,
      created_on_gteq:   filters.photoDocumentCreatedAtFrom      ? formatDate(filters.photoDocumentCreatedAtFrom,      'yyyy-MM-dd', 'de') : null,
      created_on_lteq:   filters.photoDocumentCreatedAtTo        ? formatDate(filters.photoDocumentCreatedAtTo,        'yyyy-MM-dd', 'de') : null,
      subject:           filters.photoDocumentSubject            ? this.parceSingleselect(filters.photoDocumentSubject)                    : null,
      subject_corrected: filters.photoDocumentCorrectedSubject   ? encodeURIComponent(filters.photoDocumentCorrectedSubject)               : null,
      assignment_ids:    filters.assignmentsIds                  ? this.parceMultiselect(filters.assignmentsIds)                           : null
    };
    return this.toQueryStringArray(queryObj, 'photo_documents');
  }

  private getPhotoDocumentsZvooveQuery(filters): string[] {
    let zvooveKontakt  = filters.photoDocumentReleasedToZvooveWithKontakt;
    let zvooveDokument = filters.photoDocumentReleasedToZvooveWithDokument;
    let query = '';

    if      (zvooveKontakt  && zvooveDokument)  query = '[released_to_zvoove]=true';
    else if (zvooveKontakt  && !zvooveDokument) query = '[released_to_zvoove_status]=submit_as_contact_zvoove';
    else if (!zvooveKontakt && zvooveDokument)  query = '[released_to_zvoove_status]=submit_as_doc_zvoove';

    return query ? [`q[photo_documents]${query}`] : [];
  }

  private getExternalEmployeesTableQuery(filters): string[] {
    let queryObj = {
      personal_number: filters.externalEmployeePersonalNumber ? filters.externalEmployeePersonalNumber              : null,
      ids:             filters.externalEmployeeName           ? this.parceMultiselect(filters.externalEmployeeName) : null,
    };
    return this.toQueryStringArray(queryObj, 'external_employees');
  }

  getAssignmentArchiveQuery(): string {
    let filters: any = {
      ...this.sessionStorageService.ebsArchiveForm
    };
    return '?'+[
      ...this.getAssignmentsTableQuery(filters),
      ...this.getEbsTableQuery(filters),
      ...this.getExternalEmployeesTableQuery(filters),
      ...this.getCustomerCompaniesTableQuery(filters),
      ...this.getPaginationQuery(),
      ...this.getSearchQuery(),
      ...this.getPillsQuery(),
      ...this.getSortingFlow()
    ].filter(q => q.length).join('&');
  }

  getAssignmentListQuery(): string {
    return '?'+[
      ...this.getActiveAssignmentsQuery(),
      ...this.getAssignmentsTableQuery(this.sessionStorageService.headerFiltersValue.ebs),
      ...this.getPaginationQuery(),
      ...this.getSearchQuery(),
      ...this.getPillsQuery(),
      ...this.getSortingFlow()
    ].filter(q => q.length).join('&');
  }

  getAssignmentsByTimeRangeQuery(timeRange): string {
    return '?'+[
      ...this.getAssignmentsTableQuery(timeRange),
      ...this.getPillsQuery()
    ].filter(q => q.length).join('&');
  }

  getAssignmentsExtendedQuery(filters: any = {}, betaFeature: string = null): string {
    return '?'+[
      ...this.getAssignmentsTableQuery(filters),
      ...this.getEbsTableQuery(filters),
      ...this.getExternalEmployeesTableQuery(filters),
      ...this.getCustomerCompaniesTableQuery(filters),
      ...this.getCustomersTableQuery(filters),
      ...this.getPillsQuery(),
      ...this.getSortingQuery('title'),
      ...['per_page=50',`page=1`],
      ...this.getBetaQuery(betaFeature),
    ].filter(q => q.length).join('&');
  }

  getAssignmentsPaginated(page: number, search: string = '', betaFlag: string = null): string {
    return '?'+[
      ...this.getPillsQuery(),
      ...this.getBetaQuery(betaFlag),
      ...this.getSortingQuery('title'),
      ...this.getSimplePaginationQuery(page),
      ...this.getTableSearchQuery('assignments', 'title', search)
    ].filter(q => q.length).join('&');
  }

  getCustomerCompaniesPaginated(page: number, search: string = '', betaFeature: string = null): string {
    return '?'+[
      ...this.getPillsQuery(),
      ...this.getBetaQuery(betaFeature),
      ...this.getSortingQuery('name'),
      ...this.getSimplePaginationQuery(page),
      ...this.getTableSearchQuery('customers_companies', 'name', search)
    ].filter(q => q.length).join('&');
  }

  getExternalsPaginated(page: number, search: string = '', filters: any = {}): string {
    return '?' + [
      ...this.getPillsQuery(),
      ...this.getBetaQuery(filters?.betaFeature),
      ...this.getAssignmentsTableQuery(filters, 'assignment'),
      ...this.getCustomersTableQuery(filters),
      ...this.getSortingQuery('external_employee_last_name',  0),
      ...this.getSortingQuery('external_employee_first_name', 1),
      ...this.getSimplePaginationQuery(page),
      ...this.getTableSearchQuery('external_employees', 'name', search)
    ].filter(q => q.length).join('&');
  }

  getDocumentTypesPaginated(page: number, search: string = ''): string {
    return '?'+[
      ...this.getPillsQuery(),
      ...this.getSortingQuery('name'),
      ...this.getSimplePaginationQuery(page),
      ...this.getTableSearchQuery('document_types', 'name', search)
    ].filter(q => q.length).join('&');
  }

  private getWorkingPeriodTableQuery(filters): string[] {
    let queryObj = {
      end_date_gteq:   filters.workingPeriodDateFrom      ? formatDate(filters.workingPeriodDateFrom,      'yyyy-MM-dd', 'de') : null,
      start_date_lteq: filters.workingPeriodDateTo        ? formatDate(filters.workingPeriodDateTo,        'yyyy-MM-dd', 'de') : null,
      created_on_gteq: filters.workingPeriodCreatedAtFrom ? formatDate(filters.workingPeriodCreatedAtFrom, 'yyyy-MM-dd', 'de') : null,
      created_on_lteq: filters.workingPeriodCreatedAtTo   ? formatDate(filters.workingPeriodCreatedAtTo,   'yyyy-MM-dd', 'de') : null,
      assignment_ids:  filters.assignmentsIds             ? this.parceMultiselect(filters.assignmentsIds)                      : null,
      approved:        filters.timeFramesApproved         ? filters.timeFramesApproved                                         : null,
      opened:          filters.timeFramesOpened           ? filters.timeFramesOpened                                           : null,
    };
    return this.toQueryStringArray(queryObj, 'working_periods');
  }

  private prepareWorkingPeriodStatesQuery(filters, archive: boolean = null): string[] {
    let role      = this.userService.isInternal ? 'internal' : 'customer';
    let dashboard = archive                     ? 'archive'  : 'dashboard';

    let queryObj = {
      approved:      filters.workingPeriodApproved ? filters.workingPeriodApproved : null,
      auto_archived: filters.workingPeriodArchived ? filters.workingPeriodArchived : null,
      open:          filters.workingPeriodOpened   ? filters.workingPeriodOpened   : null,
      rejected:      filters.workingPeriodRejected ? filters.workingPeriodRejected : null,
    };

    let states = [];
    for (const key in queryObj) {
      if (queryObj[key]) states.push(key);
    }

    return [`q[${role}_${dashboard}][states]=${states.join(',')}`];
  }

  private getDashboardStates(useCase: string): string[] {
    let statusFilter = this.sessionStorageService.activeTabValue?.filter;
    let filters      = {};
    if (!statusFilter) {
      filters['approved'] = true;
      filters['open']     = true;
      filters['rejected'] = true;
    } else filters[statusFilter] = true;

    let query = this.prepareDashboardStatesQuery(filters);
    if (useCase === 'ar' && this.userService.isCustomer) query.push(...this.getActiveAssignmentsQuery());
    return query;
  }

  private prepareDashboardStatesQuery(filters, archive: boolean = null): string[] {
    let role      = this.userService.isInternal ? 'internal' : 'customer';
    let dashboard = archive                     ? 'archive'  : 'dashboard';

    let states = [];
    for (const key in filters) {
      if (filters[key]) states.push(key);
    }

    return [`q[${role}_${dashboard}][states]=${states.join(',')}`];
  }


  private getWorkingPeriodArchiveStates(filters: any): string[] {
    if (!(filters['workingPeriodApproved'] || filters['workingPeriodArchived'] || filters['workingPeriodRejected'])) {
      filters['workingPeriodApproved'] = true;
      filters['workingPeriodArchived'] = true;
      filters['workingPeriodRejected'] = true;
    }

    if (this.userService.isCustomer && filters['workingPeriodArchived']) {
      filters['workingPeriodRejected'] = true;
    }

    return this.prepareWorkingPeriodStatesQuery(filters, true);
  }

  private getExtraInfoQuery(filters): string[] {
    let queryObj = {
      checked_internally: filters.extraInfoCheckedInternally ? filters.extraInfoCheckedInternally : null,
      with_attachment:    filters.extraInfoWithAttachment    ? filters.extraInfoWithAttachment    : null,
      with_mileage_money: filters.extraInfoWithMileageMoney  ? filters.extraInfoWithMileageMoney  : null,
      with_holidays:      filters.extraInfoWithHolidays      ? filters.extraInfoWithHolidays      : null
    };
    return this.toQueryStringArray(queryObj, 'additional_info');
  }

  private getAssignmentsTableQuery(filters: any, extendTable: string = ''): string[] {
    extendTable = extendTable ? extendTable+'_' : '';

    let queryObj = {
      title:                          !!filters.assignmentTitle                      ? encodeURIComponent(filters.assignmentTitle)                        : null,
      [`${extendTable}ends_on_gteq`]:   filters.assignmentDateFrom                   ? formatDate(filters.assignmentDateFrom,         'yyyy-MM-dd', 'de') : null,
      [`${extendTable}starts_on_lteq`]: filters.assignmentDateTo                     ? formatDate(filters.assignmentDateTo,           'yyyy-MM-dd', 'de') : null,
      ebs_created_on_gteq:              filters.assignmentEBSCreatedAtFrom           ? formatDate(filters.assignmentEBSCreatedAtFrom, 'yyyy-MM-dd', 'de') : null,
      ebs_created_on_lteq:              filters.assignmentEBSCreatedAtTo             ? formatDate(filters.assignmentEBSCreatedAtTo,   'yyyy-MM-dd', 'de') : null,
      active:                           this.valueNotNull(filters.assignmentActive)  ? filters.assignmentActive                                           : null,
      expired:                          this.valueNotNull(filters.assignmentExpired) ? filters.assignmentExpired                                          : null,
      assignment_ids:                   filters.assignmentsIds                       ? this.parceMultiselect(filters.assignmentsIds)                      : null,
      ebs_confirmed:                    this.userService.isCustomer || filters.ebsConfirmed && filters.ebsUnConfirmed ? null : filters.ebsConfirmed ? true : filters.ebsUnConfirmed ? false : null
    };
    return this.toQueryStringArray(queryObj, 'assignments');
  }

  private getEbsTableQuery(filters): string[] {
    if (this.userService.isCustomer) return [];
    let queryObj = {
      archived: filters.ebsArchived && filters.ebsUnArchived ? null : filters.ebsArchived ? true : filters.ebsUnArchived ? false : null
    };
    return this.toQueryStringArray(queryObj, 'ebs_data');
  }

  getCustomerCompanies(): string {
    return '?'+[
      ...this.getPaginationQuery(),
      ...this.getSearchQuery(),
      ...this.getPillsQuery(),
      ...this.getSortingFlow()
    ].filter(q => q.length).join('&');
  }

  getExternalEmployees(): string {
    return '?'+[
      ...this.getPaginationQuery(),
      ...this.getEEListTogglersState(),
      ...this.getSearchQuery(),
      ...this.getPillsQuery(),
      ...this.getSortingFlow()
    ].filter(q => q.length).join('&');
  }

  private getEEListTogglersState(): string[] {
    let dropdowns = this.sessionStorageService.headerDropdownsValue.ee_list;
    let query     = dropdowns.map(d => d.value).filter(d => d).map(d => `q[external_employees][${d}]=1`);
    return query;
  }

  getGeneralQuery({ page, perPage, search, sort }: { page: number, perPage: number, search: string, sort: SortingOptionModel }): string {
    return '?'+[
      ...this.getSimplePaginationQuery(page, perPage),
      ...this.getSimpleSearchQuery(search),
      ...this.getPillsQuery(),
      ...sort?.columns.map((c, index) => this.getSortingQuery(c, index, sort.order[index])) || []
    ].filter(q => q.length).join('&');
  }

  private getCustomersTableQuery(filters): string[] {
    let queryObj = {
      activated: filters.customersActivated ? filters.customersActivated : null,
    };
    return this.toQueryStringArray(queryObj, 'customers');
  }

  private getCustomerCompaniesTableQuery(filters): string[] {
    const queryObj = { name:            this.valueNotNull(filters.customerCompanyName)   ? this.parceSingleselect(filters.customerCompanyName)   : null };
    const queryArr = { company_numbers: this.valueNotNull(filters.customerCompanyNumber) ? this.parceSingleselect(filters.customerCompanyNumber) : null };
    return [
      ...this.toQueryStringArray(queryObj, 'customers_companies'),
      ...this.toQueryStringArray(queryArr, 'customers_companies', true),
    ];
  }

  private getTimeFramesTableQuery(filters): string[] {
    let queryObj = {
      started_on_gteq: filters.timeFrameDateFrom           ? formatDate(filters.timeFrameDateFrom, 'yyyy-MM-dd', 'de') : null,
      ended_on_lteq:   filters.timeFrameDateTo             ? formatDate(filters.timeFrameDateTo,   'yyyy-MM-dd', 'de') : null,
      time_from:       filters.timeFrameTimeFrom           ? filters.timeFrameTimeFrom + ':00'                         : null,
      time_to:         filters.timeFrameTimeTo             ? filters.timeFrameTimeTo   + ':00'                         : null,
      corrected_dates: filters.timeFramesWithCorrectedTime ? filters.timeFramesWithCorrectedTime                       : null
    };
    return this.toQueryStringArray(queryObj, 'time_frames');
  }

  private toQueryStringArray(queryObj, queryTable: string,  isArray: boolean = null): string[] {
    let queryL = [];
    for (const key in queryObj) if (queryObj[key] !== null) queryL.push(`q[${queryTable}][${key}]${isArray?'[]':''}=${queryObj[key]}`);
    return queryL;
  }

  private getFailedERPFilter(filters, erp: boolean = null): string[] {
    let togglers = erp ? 'failed_erp' : 'archive';
    let useCase  = this.sessionStorageService.headerTogglersValue[togglers].find(toggler => toggler.active).value;
    let table    = useCase === 'ar' ? 'working_periods' : 'vacation_requests';
    let paramD   = useCase === 'ar' ? 'archived'        : 'internal_reviewed';
    let query    = [];

    let loadAllERP = erp && (!filters.zvooveImportFailed && !filters.zvooveImportResolved);
    if (filters.zvooveImportSuccessfull)                    query.push(`q[${table}][export_state][]=successful_export` );
    if (filters.zvooveImportFailed           || loadAllERP) query.push(`q[${table}][export_state][]=failed_export`     );

    if (filters.zvooveImportResolvedManually || loadAllERP || filters.zvooveImportResolved) query.push(`q[${table}][export_state][]=resolved_manually` );
    if (filters.zvooveImportResolvedBySystem || loadAllERP || filters.zvooveImportResolved) query.push(`q[${table}][export_state][]=resolved_by_system`);    

    if (filters.approvedAtOrDeletedAtFrom) query.push(`q[${table}][${paramD}_on_gteq]=${formatDate(filters.approvedAtOrDeletedAtFrom, 'yyyy-MM-dd', 'de')}` );
    if (filters.approvedAtOrDeletedAtTo)   query.push(`q[${table}][${paramD}_on_lteq]=${formatDate(filters.approvedAtOrDeletedAtTo,   'yyyy-MM-dd', 'de')}` );

    return query;
  }

  private getActiveAssignmentsQuery(): string[] {
    let query = ['q[assignments][active]=true'];
    if (this.userService.isInternal) query.push('q[ebs_data][archived]=false');

    return query;
  }

  private getPaginationQuery(perPage: number = null): string[] {
    return [
      `per_page=${perPage || this.sessionStorageService.itemsPerPageValue}`,
      `page=${this.sessionStorageService.pageNumberValue}`
    ];
  }

  private getSimplePaginationQuery(page: number, perPage: number = 10): string[] {
    return [
      `per_page=${perPage}`,
      `page=${page}`
    ];
  }

  private getSearchQuery(): string[] {
    return this.getSimpleSearchQuery(this.sessionStorageService.searchValueValue);
  }

  private getSimpleSearchQuery(search: string): string[] {
    return search ? [`q[search_term]=${encodeURIComponent(search)}`] : [];
  }

  private getTableSearchQuery(table: string, field: string, search: string): string[] {
    return search ? [`q[${table}][${field}]=${encodeURIComponent(search)}`] : [];
  }

  private getSortingFlow(): string[] {
    let sortingFLow = this.sessionStorageService.sortingFlowValue;
    if (!sortingFLow || !sortingFLow.length) return [];

    let sortColumns = sortingFLow[sortingFLow.length - 1].columns;
    let sortOrder   = sortingFLow[sortingFLow.length - 1].order;

    return sortColumns.map((s, index) => this.getSortingQuery(s, index, !!sortOrder[index])[0]);
  }

  private getSortingDirection(order: boolean): string {
    return order ? 'asc' : 'desc';
  }

  private getSortingQuery(name: string, index: number = 0, dir: boolean = true): string[] {
    return [`q[sort][${index}][pos]=${index}&q[sort][${index}][name]=${name}&q[sort][${index}][dir]=${this.getSortingDirection(dir)}`];
  }

  private getBetaQuery(betaFeature: string): string[] {
    if (!betaFeature) return [];
    let tableName = this.userService.isCustomer ? 'beta_customer_companies' : 'beta_locations';
    let queryObj  = { feature: betaFeature };
    return this.toQueryStringArray(queryObj, tableName);
  }

  private getPillsQuery(): string[] {
    let pills       = this.sessionStorageService.pillsValue;
    let activePills = this.sessionStorageService.activePills;

    if (this.userService.isCustomer)                                        return activePills.map(c => c.company_number).map(c => `q[customers_companies][company_numbers][]=${c}`);
    if (this.userService.isInternal && pills.length !== activePills.length) return activePills.map(c => c.location).map(l => `q[locations][]=${l}`);
    else return [];
  }

  private getCompaniesInvoiceQuery(): string[] {
    return this.sessionStorageService.activePills.map(c => c.company_number).map(c => `q[customers_companies][company_numbers][]=${c}`);
  }

  getInvoicesQuery(): string {
    return '?'+[
      ...this.getPaginationQuery(),
      ...this.getSearchQuery(),
      ...this.getCompaniesInvoiceQuery(),
      ...this.getSortingFlow()
    ].filter(q => q.length).join('&');
  }

  getInvoicesArchiveQuery() {
    return '?'+[
      ...this.getPaginationQuery(),
      ...this.getSearchQuery(),
      ...this.getCompaniesInvoiceQuery(),
      ...this.getSortingFlow()
    ].filter(q => q.length).join('&');
  }

  getReleasesQuery() {
    return '?'+[
      ...this.getPaginationQuery(),
      ...this.getSearchQuery(),
      ...this.getSortingFlow()
    ].filter(q => q.length).join('&');
  }

  getVacationRequestsQuery(): string {
    return '?'+[
      ...this.getDashboardStates('vr'),
      ...this.getPaginationQuery(),
      ...this.getSearchQuery(),
      ...this.getPillsQuery(),
      ...this.getSortingFlow()
    ].filter(q => q.length).join('&');
  }

  private getVacationRequestTableQuery(filters): string[] {
    let queryObj = {
      created_on_gteq:    filters.vacationRequestCreatedAtFrom ? formatDate(filters.vacationRequestCreatedAtFrom, 'yyyy-MM-dd', 'de')  : null,
      created_on_lteq:    filters.vacationRequestCreatedAtTo   ? formatDate(filters.vacationRequestCreatedAtTo,   'yyyy-MM-dd', 'de')  : null,
      starts_on_gteq:     filters.vacationRequestStartsOn      ? formatDate(filters.vacationRequestStartsOn,      'yyyy-MM-dd', 'de')  : null,
      ends_on_lteq:       filters.vacationRequestEndsOn        ? formatDate(filters.vacationRequestEndsOn,        'yyyy-MM-dd', 'de')  : null,
      leave_type:         filters.vacationRequestLeaveType     ? this.parceSingleselect(filters.vacationRequestLeaveType)              : null,
      reason:             filters.vacationRequestReason        ? this.parceSingleselect(filters.vacationRequestReason)                 : null,
      creation_initiator: filters.vacationRequestInitiator     ? this.parceSingleselect(filters.vacationRequestInitiator)              : null,
    };
    return this.toQueryStringArray(queryObj, 'vacation_requests');
  }

  private getVacationRequestArchiveTableQuery(filters): string[] {
    let queryObj = {
      assignment_ids:         filters.assignmentsIds             ? this.parceMultiselect(filters.assignmentsIds) : null,
      prechecked_by_internal: filters.extraInfoCheckedInternally ? filters.extraInfoCheckedInternally            : null,
      with_holidays:          filters.extraInfoWithHolidays      ? filters.extraInfoWithHolidays                 : null,
      creator:                filters.extraInfoCreatedByInternal ? 'Internal Employee'                           : null
    };
    return this.toQueryStringArray(queryObj, 'vacation_requests');
  }

  private getCheckboxStateFilter(filters, useCase: string): string[] {
    let board, states = [];
    if (this.userService.isCustomer) board = 'customer_archive';
    if (this.userService.isInternal) board = 'internal_archive';

    let fields = this.getCheckboxFieldNames(useCase);
    for (const key in fields) { if (filters[fields[key]]) states.push(key); }

    if (useCase !== 'photo_documents' && !states.length) states = Object.keys(fields);
    else if (this.userService.isCustomer && filters[fields['rejected']]) states.push('auto_archived');

    return states?.length ? [`q[${board}][states]=${states.filter(s => s).join(',') }`] : [];
  }

  private getCheckboxFieldNames(useCase: string): { approved?: string, rejected?: string, auto_archived?: string} {
    switch (useCase) {
      case 'vacation_requests': return { auto_archived: 'vacationRequestArchived', approved: 'vacationRequestApprovedByInternal', rejected: 'vacationRequestRejectedByInternal' };
      case 'mileage_money':     return { auto_archived: 'mileageMoneyArchived',    approved: 'mileageMoneyApproved',              rejected: 'mileageMoneyRejected'              };
      case 'photo_documents':   return { auto_archived: 'photoDocumentArchived' };
      default:
        break;
    }
  }

  getVacationRequestsArchiveQuery() {
    let filters: any = { ...this.sessionStorageService.vacationRequestsArchiveForm };
    filters.betaFeature = 'vacation_requests';

    return '?'+[
      ...this.getVacationRequestTableQuery(filters),
      ...this.getVacationRequestArchiveTableQuery(filters),
      ...this.getCheckboxStateFilter(filters, 'vacation_requests'),
      ...this.getFailedERPFilter(filters),
      ...this.getAssignmentsTableQuery(filters, 'assignment'),
      ...this.getExternalEmployeesTableQuery(filters),
      ...this.getPaginationQuery(),
      ...this.getSortingFlow(),
      ...this.getPillsQuery()
    ].filter(q => q.length).join('&');
  }

  getMileageReportsQuery(): string {
    return '?'+[
      ...this.getPaginationQuery(),
      ...this.getSearchQuery(),
      ...this.getPillsQuery(),
      ...this.getSortingFlow(),
      ...['q[internal_dashboard][states]=open']
    ].filter(q => q.length).join('&');
  }

  getMileageReportsArchiveQuery() {
    let filters: any = { ...this.sessionStorageService.mileageMoneyArchiveForm };
    return '?'+[
      ...this.getMileageMoneyTableQuery(filters),
      ...this.getCheckboxStateFilter(filters, 'mileage_money'),
      ...this.getExternalEmployeesTableQuery(filters),
      ...this.getPaginationQuery(),
      ...this.getSortingFlow(),
      ...this.getPillsQuery(),
      ...this.getBetaQuery('standalone_mileage_reports')
    ].filter(q => q.length).join('&');
  }

  getSearchTermHistory(staringWith: string, type: string = 'string'): string {
    const queryObj = {
      entity_type: type,
      search_term_start: staringWith
    };
    return '?' + [...this.toQueryStringArray(queryObj, 'search_term_histories')].join('&');
  }

  private getMileageMoneyTableQuery(filters): string[] {
    let queryObj = {
      created_on_gteq: filters.mileageMoneyCreatedAtFrom ? formatDate(filters.mileageMoneyCreatedAtFrom, 'yyyy-MM-dd', 'de') : null,
      created_on_lteq: filters.mileageMoneyCreatedAtTo   ? formatDate(filters.mileageMoneyCreatedAtTo,   'yyyy-MM-dd', 'de') : null,
      end_date_gteq:   filters.mileageMoneyStartsOn      ? formatDate(filters.mileageMoneyStartsOn,      'yyyy-MM-dd', 'de') : null,
      start_date_lteq: filters.mileageMoneyEndsOn        ? formatDate(filters.mileageMoneyEndsOn,        'yyyy-MM-dd', 'de') : null,
      assignment_ids:  filters.assignmentsIds            ? this.parceMultiselect(filters.assignmentsIds)                     : null,
      resource_type:   this.collectCheckboxes(filters.standaloneMileageReportType,    'standalone_mileage_report',
                                              filters.workingPeriodMileageReportType, 'working_period_mileage_report')
    };
    return this.toQueryStringArray(queryObj, 'mileage_reports');
  }

  private getReferralsTableQuery(filters): string[] {
    const queryObj = {
      created_on_gteq: filters.createDateFrom        ? formatDate(filters.createDateFrom, 'yyyy-MM-dd', 'de') : null,
      created_on_lteq: filters.createDateTo          ? formatDate(filters.createDateTo, 'yyyy-MM-dd', 'de')   : null,
      hiring_state_eq: filters.referralStatusSubject ? this.parceSingleselect(filters.referralStatusSubject)  : null,
    };

    return this.toQueryStringArray(queryObj, 'referral_program_referees');
  }

  getReferralsQuery(): string {
    return '?'+[
      ...this.getPaginationQuery(),
      ...this.getSearchQuery(),
      ...this.getPillsQuery(),
      ...this.getSortingFlow(),
    ].filter(q => q.length).join('&');
  }

  getArchiveReferralsQuery(): string {
    let filters = {
      ...this.sessionStorageService.referralsArchiveForm
    };
    return '?'+[
      ...this.getPaginationQuery(),
      ...this.getSearchQuery(),
      ...this.getPillsQuery(),
      ...this.getReferralsTableQuery(filters),
      ...this.getExternalEmployeesTableQuery(filters),
      ...this.getSortingFlow(),
    ].filter(q => q.length).join('&');
  }

  private getContractExtensionsTableQuery(filters:any): string[] {
    const queryObj = {
      created_on_gteq: filters.createDateFrom        ? formatDate(filters.createDateFrom, 'yyyy-MM-dd', 'de') : null,
      created_on_lteq: filters.createDateTo          ? formatDate(filters.createDateTo, 'yyyy-MM-dd', 'de')   : null,
      assignment_ids:  filters.assignmentsIds        ? this.parceMultiselect(filters.assignmentsIds) : null,
    };
    return this.toQueryStringArray(queryObj, 'contract_extensions');
  }

  getContractExtensionQuery(): string {
    let filters = {
      ...this.sessionStorageService.contractExtensionsForm
    };
    return '?'+[
      ...this.getPaginationQuery(),
      ...this.getSearchQuery(),
      ...this.getPillsQuery(),
      ...this.getExternalEmployeesTableQuery(filters),
      ...this.getContractExtensionsTableQuery(filters),
      ...this.getCustomerCompaniesTableQuery(filters),
      // ...this.getCustomerCompaniesNumbersTableQuery(filters),
      ...this.getSortingFlow(),
    ].filter(q => q.length).join('&');
  }

  getRoomsQuery(page: number, search: string): string {
    return '?'+[
      ...this.getBetaQuery('chat_feature'),
      ...this.getPillsQuery(),
      ...this.getSimplePaginationQuery(page),
      ...this.getSimpleSearchQuery(search),
      ...this.getSortingQuery('participant_priority', 0, true),
      ...this.getSortingQuery('last_message_at',      1, false),
    ].filter(q => q.length).join('&');
  }

  getMessagesQuery(page: number): string {
    return '?'+[
      ...this.getSimplePaginationQuery(page, 10),
      ...this.getSortingQuery('id', 0, false),
    ].filter(q => q.length).join('&');
  }

  getBulkMessagesQuery(page: number, search: string, kinds: string[] = []): string {
    return '?'+[
      ...this.getSimplePaginationQuery(page, 10),
      ...this.getSimpleSearchQuery(search),
      ...this.getSortingQuery('created_at', 0, false),
      ...this.getBulkMessagesTableQuery(kinds)
    ].filter(q => q.length).join('&');
  }

  private getBulkMessagesTableQuery(kinds: string[]): string[] {
    let queryObj = {
      kind: kinds?.length ? kinds.join(',') : null
    };
    return this.toQueryStringArray(queryObj, 'bulk_messages');
  }

  private collectCheckboxes(a: boolean, aValue: string, b: boolean, bValue: string): string {
    if ( a && !b) return aValue;
    if (!a &&  b) return bValue;
    if (!a && !b) return null;
  }

  private getTogglerStateFilter(toggler: string): string[] {
    let board;
    if (this.userService.isCustomer) board = 'customer_dashboard';
    if (this.userService.isInternal) board = 'internal_dashboard';

    let togglers = this.sessionStorageService.headerTogglersValue[toggler] || null;
    let state    = togglers.find(tog => tog.active);
    return state ? [`q[${board}][states]=${state.value}`] : [];
  }

  private valueNotNull(value: any): boolean {
    return value !== undefined && value !== null;
  }

  private parceSingleselect(value: any): string {
    return encodeURIComponent(value.value);
  }

  private parceMultiselect(value: any): string {
    return encodeURIComponent(value.split(';').map(e => e.split('_')[0]).join(','));
  }

  getCandidatesKundenQuery(): string[] {
    let activePills = this.sessionStorageService.activePills;
    return activePills.map(c => `kunden_nr=${c.company_number}`);
  }

  getCandidatesQuery(): string {
    return '?' + [
      ...this.getCandidatesKundenQuery(),
    ].filter(q => q.length).join('&');
  }

}
